import React, { ReactNode } from 'react';
import moment from 'moment';
import Color from 'color';
import {
    DateField,
    TextField,
    ShowButton,
    Filter,
    SelectInput,
    ReferenceInput,
    DateInput,
    Link,
    FunctionField,
} from 'react-admin';
import CustomizableGrid from '../components/CustomizableGrid';
import { ColumnConfig } from '../hooks/useColumnReorder';
import CustomSearchInput from '../components/CustomSearchInput';
import { OrderStatus, OrderStatusColor, OrderType, Source } from '../constants';
import ChipField from '../components/ChipField';
import BoxitChipField from '../components/BoxitChipField';
import CityField from '../components/CityField';
import BoxitPagination from '../components/BoxitPagination';
import UserReferenceField from '../components/UserReferenceField';
import { getOrderColor, getButtonColor } from '../orders/utils';
import { SelectArrayInput } from 'react-admin';
import { Chip } from '@material-ui/core';
import humanize from '../util/humanize';

const rowStyle = record => {
    if (
        [
            OrderStatus.complete,
            OrderStatus.rejected,
            OrderStatus.cancelled,
            OrderStatus.lost,
        ].indexOf(record.status) !== -1
    ) {
        return {};
    }

    let leadColor = getOrderColor(record);

    return {
        backgroundColor: Color(leadColor).alpha(0.1),
    };
};
// Custom field components with links
const PartnerField = ({ record }) => {
    if (!record || !record.partnerName) return null;
    return (
        <Link to={`/partners/${record.partnerId}/show`}>
            {record.partnerName}
        </Link>
    );
};

const CityNameField = ({ record }) => {
    if (!record || !record.cityName) return null;
    return <span>{record.cityName}</span>;
};

const CustomerNameField = ({ record }) => {
    if (!record || !record.userName) return null;
    return <Link to={`/users/${record.userId}/show`}>{record.userName}</Link>;
};

const AssigneeField = ({ record }) => {
    if (!record || !record.assigneeId || !record.assigneeName) return null;
    return (
        <Link to={`/users/${record.assigneeId}/show`}>
            {record.assigneeName}
        </Link>
    );
};

const PhoneField = ({ record }) => {
    if (!record || !record.userPhone) return null;
    return <span>{record.userPhone}</span>;
};

const LeadFilter = props => {
    const { permissions = [] } = props;
    const isAdmin = permissions.indexOf('admin') !== -1;
    return (
        <Filter variant="outlined" {...props}>
            <CustomSearchInput alwaysOn />

            <SelectArrayInput
                style={{ minWidth: '10rem' }}
                source="type"
                choices={Object.keys(OrderType).map(c => ({
                    id: c,
                    name: humanize(c),
                }))}
                parse={value => value}
                format={value =>
                    Array.isArray(value) ? value : value ? [value] : []
                }
            />

            <ReferenceInput
                source="assigneeId"
                reference="users"
                resource="users"
                label="Assignee"
                filter={
                    isAdmin
                        ? { userRoles: ['sales-staff'] }
                        : {
                              userRoles: ['sales-staff'],
                              countryId: props.countryId,
                          }
                }
                perPage={50}
            >
                <SelectArrayInput
                    style={{ minWidth: '10rem' }}
                    parse={value => value}
                    format={value =>
                        Array.isArray(value) ? value : value ? [value] : []
                    }
                />
            </ReferenceInput>

            <ReferenceInput
                source="partnerId"
                reference="partners"
                resource="partners"
                label="Partner"
            >
                <SelectArrayInput
                    style={{ minWidth: '10rem' }}
                    parse={value => value}
                    format={value =>
                        Array.isArray(value) ? value : value ? [value] : []
                    }
                />
            </ReferenceInput>

            <ReferenceInput
                source="countryId"
                reference="country"
                resource="country"
                label="Country"
            >
                <SelectInput />
            </ReferenceInput>

            <ReferenceInput
                source="cityId"
                reference="city"
                resource="city"
                label="City"
            >
                <SelectInput />
            </ReferenceInput>

            <SelectArrayInput
                style={{ minWidth: '10rem' }}
                source="status"
                choices={Object.keys(OrderStatus).map(c => ({
                    id: c,
                    name: c,
                }))}
            />

            <DateInput
                source="date[$gte]"
                label="Date From"
                parse={v => v && moment(v).format('YYYY-MM-DD')}
            />
            <DateInput
                source="date[$lte]"
                label="Date To"
                parse={v => v && moment(v).format('YYYY-MM-DD')}
            />

            <DateInput
                source="quoteSentAt[$gte]"
                label="Quote Sent From"
                parse={v => v && moment(v).startOf('day').format()}
            />
            <DateInput
                source="quoteSentAt[$lte]"
                label="Quote Sent To"
                parse={v => v && moment(v).endOf('day').format()}
            />

            <DateInput
                source="createdAt[$gte]"
                label="Created From"
                parse={v => v && moment(v).startOf('day').format()}
            />
            <DateInput
                source="createdAt[$lte]"
                label="Created To"
                parse={v => v && moment(v).endOf('day').format()}
            />
            <SelectInput
                source="source"
                choices={Object.keys(Source).map(c => ({
                    id: c,
                    name: Source[c],
                }))}
            />
        </Filter>
    );
};

const LeadList = ({ permissions = [], ...props }) => {
    // Define default columns configuration
    const defaultColumns: ColumnConfig[] = [
        // Default visible columns (essential information)
        { id: 'serial', label: 'Serial', visible: true, order: 0 },
        { id: 'id', label: 'ID', visible: true, order: 1 },
        { id: 'createdAt', label: 'Created', visible: true, order: 2 },
        { id: 'partner', label: 'Partner', visible: true, order: 3 },
        { id: 'assignee', label: 'Assignee', visible: true, order: 4 },
        { id: 'customer', label: 'Customer', visible: true, order: 5 },
        { id: 'phone', label: 'Phone', visible: true, order: 6 },
        { id: 'city', label: 'City', visible: true, order: 7 },
        { id: 'type', label: 'Type', visible: true, order: 8 },
        { id: 'date', label: 'Date', visible: true, order: 9 },
        { id: 'status', label: 'Status', visible: true, order: 10 },
        { id: 'source', label: 'Source', visible: true, order: 11 },
        { id: 'show', label: 'Show', visible: true, order: 12 },

        // Additional fields from API response (initially hidden/optional)
        { id: 'reference', label: 'Reference', visible: false, order: 13 },
        { id: 'updatedAt', label: 'Updated', visible: false, order: 14 },
        { id: 'userEmail', label: 'Email', visible: false, order: 15 },
        { id: 'countryName', label: 'Country', visible: false, order: 16 },
        { id: 'storageType', label: 'Storage Type', visible: false, order: 17 },
        { id: 'renewalDate', label: 'Renewal Date', visible: false, order: 18 },
        { id: 'quoteSentAt', label: 'Quote Sent', visible: false, order: 19 },
        { id: 'followUpAt', label: 'Follow Up', visible: false, order: 20 },
        { id: 'currency', label: 'Currency', visible: false, order: 21 },
        { id: 'amount', label: 'Amount', visible: false, order: 22 },
        {
            id: 'customerRent',
            label: 'Customer Rent',
            visible: false,
            order: 23,
        },
        { id: 'rent', label: 'Rent', visible: false, order: 24 },
        { id: 'rate', label: 'Rate', visible: false, order: 25 },
        { id: 'months', label: 'Months', visible: false, order: 26 },
        { id: 'size', label: 'Size', visible: false, order: 27 },
        { id: 'actualQty', label: 'Actual Qty', visible: false, order: 28 },
        { id: 'currentQty', label: 'Current Qty', visible: false, order: 29 },
        { id: 'currentSize', label: 'Current Size', visible: false, order: 30 },
        { id: 'currentRent', label: 'Current Rent', visible: false, order: 31 },
    ];

    // Function to render column based on column ID
    const renderColumn = (columnId: string): ReactNode => {
        switch (columnId) {
            case 'serial':
                return (
                    <TextField
                        key={columnId}
                        source="serial"
                        sortable={false}
                    />
                );
            case 'id':
                return <TextField key={columnId} source="id" />;
            case 'createdAt':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="createdAt"
                        label="Created"
                        showTime
                    />
                );
            case 'partner':
                return (
                    <FunctionField
                        key={columnId}
                        label="Partner"
                        render={record => <PartnerField record={record} />}
                    />
                );
            case 'assignee':
                return (
                    <FunctionField
                        key={columnId}
                        label="Assignee"
                        render={record => <AssigneeField record={record} />}
                    />
                );
            case 'customer':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer"
                        render={record => <CustomerNameField record={record} />}
                    />
                );
            case 'phone':
                return (
                    <FunctionField
                        key={columnId}
                        label="Phone"
                        render={record => <PhoneField record={record} />}
                        sortable={false}
                    />
                );
            case 'city':
                return (
                    <FunctionField
                        key={columnId}
                        label="City"
                        render={record => <CityNameField record={record} />}
                    />
                );
            case 'type':
                return (
                    <FunctionField
                        key={columnId}
                        label="Type"
                        render={record => {
                            return (
                                <Chip
                                    label={humanize(
                                        record.orderType || record.type
                                    )}
                                />
                            );
                        }}
                    />
                );
            case 'storageType':
                return <ChipField key={columnId} source="storageType" />;
            case 'date':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="date"
                        label="Date"
                    />
                );
            case 'quoteSentAt':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="quoteSentAt"
                        label="Quote Sent"
                    />
                );
            case 'followUpAt':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="followUpAt"
                        label="Follow Up"
                    />
                );
            case 'reference':
                return <TextField key={columnId} source="reference" />;
            case 'updatedAt':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="updatedAt"
                        label="Updated"
                        showTime
                    />
                );
            case 'userEmail':
                return (
                    <TextField
                        key={columnId}
                        source="userEmail"
                        label="Email"
                    />
                );
            case 'countryName':
                return (
                    <TextField
                        key={columnId}
                        source="countryName"
                        label="Country"
                    />
                );
            case 'renewalDate':
                return (
                    <DateField
                        key={columnId}
                        locales="en-IN"
                        source="renewalDate"
                        label="Renewal Date"
                    />
                );
            case 'currency':
                return <TextField key={columnId} source="currency" />;
            case 'amount':
                return (
                    <FunctionField
                        key={columnId}
                        label="Amount"
                        render={record => {
                            if (!record.amount) return '-';
                            return `${record.currency || ''} ${(
                                record.amount / 1000
                            ).toFixed(3)}`;
                        }}
                    />
                );
            case 'customerRent':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Rent"
                        render={record => {
                            if (!record.customerRent) return '-';
                            return `${record.currency || ''} ${(
                                record.customerRent / 1000
                            ).toFixed(3)}`;
                        }}
                    />
                );
            case 'rent':
                return (
                    <FunctionField
                        key={columnId}
                        label="Rent"
                        render={record => {
                            if (!record.rent) return '-';
                            return `${record.currency || ''} ${(
                                record.rent / 1000
                            ).toFixed(3)}`;
                        }}
                    />
                );
            case 'rate':
                return (
                    <FunctionField
                        key={columnId}
                        label="Rate"
                        render={record => {
                            if (!record.rate) return '-';
                            return `${record.currency || ''} ${(
                                record.rate / 1000
                            ).toFixed(3)}`;
                        }}
                    />
                );
            case 'months':
                return <TextField key={columnId} source="months" />;
            case 'size':
                return <TextField key={columnId} source="size" />;
            case 'actualQty':
                return (
                    <TextField
                        key={columnId}
                        source="actualQty"
                        label="Actual Qty"
                    />
                );
            case 'currentQty':
                return (
                    <TextField
                        key={columnId}
                        source="currentQty"
                        label="Current Qty"
                    />
                );
            case 'currentSize':
                return (
                    <TextField
                        key={columnId}
                        source="currentSize"
                        label="Current Size"
                    />
                );
            case 'currentRent':
                return (
                    <FunctionField
                        key={columnId}
                        label="Current Rent"
                        render={record => {
                            if (!record.currentRent) return '-';
                            return `${record.currency || ''} ${(
                                record.currentRent / 1000
                            ).toFixed(3)}`;
                        }}
                    />
                );
            case 'status':
                return (
                    <BoxitChipField
                        key={columnId}
                        record
                        source="status"
                        list="order"
                    />
                );
            case 'source':
                return <ChipField key={columnId} source="source" />;
            case 'show':
                return <ShowButton key={columnId} style={{ padding: 0 }} />;
            default:
                return null;
        }
    };

    // Ensure we have all required props, but avoid duplicates with what might be in props
    const combinedProps = {
        title: 'Leads',
        permissions,
        defaultColumns,
        storageKey: 'leadListColumns',
        renderColumn,
        bulkActionButtons: false,
        filtersComponent: <LeadFilter permissions={permissions} />,
        sort: { field: 'id', order: 'DESC' },
        paginationComponent: <BoxitPagination />,
        perPage: 10,
        rowStyle: rowStyle,
        ...props,
        // Ensure these are set if not in props
        resource: props.resource || 'leads',
        location: props.location || { search: '' },
        hasShow: props.hasShow !== undefined ? props.hasShow : true,
    };

    return <CustomizableGrid {...combinedProps} />;
};

LeadList.defaultProps = {
    resource: 'leads',
    location: { search: '' },
};

export default LeadList;
